import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Formik, FormikProps, Form, FormikHelpers } from "formik";
import { Grid, Button, styled, alpha, ButtonGroup, Chip } from "@mui/material";
// Formik Fields
import { FormikSelectField, FormikTextField } from "../../../MUI5FormikComponents/FormikWrappers";
import {
  IPairingLogicModifier,
  ILogicModifier,
  ICrewLogicModifier,
  LogicModifierAllowableOnTypes,
} from "@vmosolutions/oc-interfaces";
import { IEventGroupOptions } from "../Tabs/LogicModifiersTab";
import * as Yup from "yup";
import { getBusinessLogicModifiers } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { CustomFieldsV2, computeCustomFieldErrors } from "src/components/Custom/CustomFieldsV2";
import _ from "lodash";
import { unflattenObj } from "src/utils/index";
import { IEmbeddedFormState } from "../PairingEditorWindow";
import { IExtendedJSONSchema6 } from "src/components/Custom/CustomFieldsV2";
import { invertHexColor } from "src/pages/FlightTimeline/TimelineUtils";
import { AddBox } from "@mui/icons-material";

interface ILogicModifierFormProps {
  mosaicPane: HTMLElement;
  logicModifier: IPairingLogicModifier | ICrewLogicModifier;
  setSelectedLogicModifier?: (pairingLM: IPairingLogicModifier | null) => void;
  updateLogicModifier: (logicModifier: IPairingLogicModifier | ICrewLogicModifier, isNew: boolean) => void;
  eventGroupOptions?: IEventGroupOptions[];
  setEmbeddedFormStates?: React.Dispatch<React.SetStateAction<IEmbeddedFormState>>;
  isPairingEditor?: true;
  contextMenuForm?: boolean;
}

// New Logic Modifier default values.
export const initialValues: IPairingLogicModifier = {
  _id: undefined,
  logicModifier: undefined,
  dutyTime: undefined,
  note: undefined,
  crewNote: undefined,
};

export const logicModifierFieldsSchema = Yup.object().shape({
  _id: Yup.string().nullable(),
  logicModifier: Yup.object().nullable().required(),
  dutyTime: Yup.number().nullable(),
  note: Yup.string().nullable(),
});

export type ILogicModifierForm = FormikProps<IPairingLogicModifier> | FormikProps<ICrewLogicModifier>;

const defaultEventGroup: IEventGroupOptions[] = [
  {
    onRest: false,
    start: null,
    end: null,
    dutyTime: null,
    value: null,
    label: "Pairing",
  },
];

const OCLogicModifierForm = styled("div")(({ theme }) => ({
  "& .formik_field_container": {
    "& .formik_field": {
      maxWidth: 350,
    },
    "& .pairing_note_textfield": {
      "& .input_control label": { width: 180 },
    },
    "& p.required_fields_label": {
      margin: 0,
      fontWeight: 300,
      fontSize: 10,
      textTransform: "uppercase",
      color: theme.custom.forms.labels,
    },
    "& .required_fields": {
      backgroundColor: alpha(theme.custom.forms.labels, 0.2),
      margin: "2px 0px",
    },
    "& .form_actions": {
      margin: 10,
      "& button": {
        margin: "0px 2px",
        fontSize: 11,
        lineHeight: "12px",
      },
    },
  },
  "& .event_dropzone": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    flexGrow: 1,
    border: `1px dashed ${theme.custom.forms.labels}`,
    backgroundColor: theme.custom.timeline.contextListBackground,
    color: theme.palette.primary.contrastText,
    borderRadius: 3,
    padding: 20,
    marginBottom: "10px",
    "& .title": { fontSize: 18, fontWeight: "bold" },
    "& .body": { fontSize: 14, fontWeight: 300, textAlign: "center" },
  },
}));

/**
 * Return selected duty group if the duty time is between the start and end time of available option
 * @param selectedDutyTime - dutytime is selected option
 * @param fullEventGroupOptions - fullevent group options
 * @returns
 */
const returnSelectedDutyGroup = (
  selectedDutyTime: number,
  fullEventGroupOptions: IEventGroupOptions[]
): IEventGroupOptions | null => {
  return fullEventGroupOptions?.length && selectedDutyTime !== undefined
    ? fullEventGroupOptions?.find((pairingLM: IEventGroupOptions) => {
        return (
          selectedDutyTime === pairingLM.dutyTime ||
          (selectedDutyTime &&
            pairingLM?.start &&
            pairingLM?.end &&
            selectedDutyTime >= pairingLM?.start &&
            selectedDutyTime <= pairingLM?.end)
        );
      })
    : null;
};

/** New function assigned a selected logic modifier and ensures any default schema values are assigned to custom property */
const assignLogicModifier = (formProps: ILogicModifierForm, selectedLogicModifier: ILogicModifier): void => {
  const defaultValues: { [key: string]: any } = {};
  const selectedShema =
    !!selectedLogicModifier?.customSchema && typeof selectedLogicModifier?.customSchema === "string"
      ? JSON.parse(selectedLogicModifier?.customSchema)
      : null;
  // Loop through all custom properties and assign any default values they contain.
  if (selectedShema?.properties) {
    Object.keys(selectedShema.properties).forEach((value: string) => {
      defaultValues[value] =
        selectedShema.properties[value].default ?? selectedShema.properties[value]?.selectorProps?.defaultValue;
    });
  }
  const hasDefaultValues = Object.entries(defaultValues)?.length > 0;
  const newValue: ILogicModifier = hasDefaultValues
    ? { ...selectedLogicModifier, custom: defaultValues }
    : selectedLogicModifier;
  // Only the first call validates. Formik validates against the values snapshot of the render that
  // produced `formProps`, so a validation pass triggered by any of the later calls would run the
  // previous selection's custom values against the new schema and, resolving last, overwrite the
  // correct result (a required field with a default showed "required"; a required field cleared by
  // switching modifiers showed no error).
  formProps.setFieldValue("logicModifier", newValue, true);
  formProps.setFieldTouched("logicModifier", true, false);
  formProps.setFieldValue("dutyTime", null, false);
  formProps.setFieldTouched("dutyTime", true, false);
};

export const LogicModifierForm = (props: ILogicModifierFormProps): JSX.Element => {
  const {
    mosaicPane,
    logicModifier,
    setSelectedLogicModifier,
    updateLogicModifier,
    eventGroupOptions,
    setEmbeddedFormStates,
    isPairingEditor,
    contextMenuForm = false,
  } = props;
  const [options, setOptions] = useState<ILogicModifier[]>([]);
  const [customSchema, setCustomSchema] = useState<IExtendedJSONSchema6 | null>(null);
  // Formik's `validate` runs synchronously inside the same handler that swaps the schema, before
  // React has re-rendered, so a plain state closure would validate against the previous schema.
  // The ref is updated synchronously so validation always sees the schema for the current selection.
  const customSchemaRef = useRef<IExtendedJSONSchema6 | null>(null);
  const updateCustomSchema = useCallback((schema: IExtendedJSONSchema6 | null): void => {
    customSchemaRef.current = schema;
    setCustomSchema(schema);
  }, []);

  useEffect(() => {
    let isCancelled = false;
    const getBusinesLogicModifiersAction = async (): Promise<void> => {
      const blms = await getBusinessLogicModifiers();
      if (!isCancelled) {
        setOptions(blms);
      }
    };
    getBusinesLogicModifiersAction();
    return () => {
      isCancelled = true;
    };
  }, []);

  const logicModifierOptions = useMemo(() => {
    const lmOptions = eventGroupOptions
      ? options.filter(
          (opt) =>
            !(opt.allowableOnPairing === false && opt.allowableOn === LogicModifierAllowableOnTypes.none) &&
            opt.isActive
        )
      : options.filter((opt) => opt.allowableOnDay !== false && opt.isActive);
    return lmOptions.sort((a, b) => {
      return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
    });
  }, [options, eventGroupOptions]);

  const fullEventGroupOptions: IEventGroupOptions[] = useMemo(
    () => (eventGroupOptions ? [...defaultEventGroup, ...eventGroupOptions] : defaultEventGroup),
    [eventGroupOptions]
  );

  // Get the schema for actively selected logicModifier
  const getSchema = useCallback(
    (logicModifier: ILogicModifier | null): void => {
      let schema = logicModifier?.customSchema ? JSON.parse(logicModifier?.customSchema) : null;
      if (!schema) {
        const result = logicModifierOptions?.find((lm: ILogicModifier) => lm?._id === logicModifier?._id);
        schema = result?.customSchema ? JSON.parse(result?.customSchema) : null;
      }
      updateCustomSchema(schema);
    },
    [logicModifierOptions, updateCustomSchema]
  );

  /** If form is modified, reset or submitted, communicate it's "dirty" state to pairing form */
  const resetForm = useCallback(
    (formProps: ILogicModifierForm) => {
      updateCustomSchema(null);
      if (setSelectedLogicModifier) setSelectedLogicModifier(null);
      //@ts-ignore
      formProps.resetForm({ values: initialValues });
    },
    [setSelectedLogicModifier, setEmbeddedFormStates, updateCustomSchema]
  );

  useEffect(() => {
    // Retrieve schema for initial value
    if (logicModifier?.logicModifier?.customSchema) {
      getSchema(logicModifier?.logicModifier);
    } else {
      // If no schema is present, reset to null.
      updateCustomSchema(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logicModifier]);

  const handleSubmit = useCallback(
    (values: IPairingLogicModifier, helpers: FormikHelpers<IPairingLogicModifier>): void => {
      const logicMod = _.cloneDeep(values);
      updateCustomSchema(null);
      if (setSelectedLogicModifier) setSelectedLogicModifier(null);
      helpers.resetForm({ values: initialValues }); // Reset form on submit.
      updateLogicModifier(logicMod, !logicMod._id);
    },
    [setSelectedLogicModifier, updateLogicModifier, updateCustomSchema]
  );

  return (
    <OCLogicModifierForm>
      <Formik
        initialValues={logicModifier}
        enableReinitialize={true}
        onSubmit={handleSubmit}
        validateOnBlur={true}
        validate={(values) =>
          unflattenObj(
            computeCustomFieldErrors(customSchemaRef.current, values?.logicModifier?.custom, "logicModifier.custom")
          )
        }
      >
        {(formProps: ILogicModifierForm) => {
          const { isValid, values } = formProps;
          const formTitle = ` ${values?._id ? "Edit " : "Add  New "} Logic Modifier`;

          /**
           * Checks current logic modifier is for duty scope, not allowed for pairings, and if dutyTime is null.
           * Null check for dutyTime represents whether or not the user has manually selected an event from event group drop-down menu. A null value in this case means the option was left as the default.
           *
           * @returns true if logic modifier is only for duty scope and event group selection is default, else false
           */
          const isDutyOnlyAndDefaultEvent = (): boolean =>
            values.logicModifier.allowableOn === "duty" &&
            values.logicModifier.allowableOnPairing === false &&
            ("dutyTime" in values ? values?.dutyTime === null : false);

          /**
           * Based on the current Logic Modifier, checks if the current event group option needs to be disabled.
           * If values.logicModifier is not falsy, the option goes through 3 checks:
           *   - If it's a pairing and if pairings are applicable
           *   - If it's a rest event and if rest events are allowed
           *   - If it's a duty event and if duty events are allowed
           * @param option - the event option to check
           * @returns true if the option is to be disabled from the event group list; false if the option should be shown
           */
          const isEventGroupOptionDisabled = (option: IEventGroupOptions): boolean => {
            if (!values?.logicModifier) return true;

            const isRestAllowed = values?.logicModifier?.allowableOn === LogicModifierAllowableOnTypes.rest;

            if (option.label === "Pairing" && formProps?.values?.logicModifier?.allowableOnPairing === false)
              return true;
            else if (option.onRest && values?.logicModifier?.allowableOn !== LogicModifierAllowableOnTypes.rest)
              return true;
            else if (
              option.label !== "Pairing" &&
              !option.onRest &&
              values?.logicModifier?.allowableOn !== LogicModifierAllowableOnTypes.duty &&
              !(option.containsOnDutyRest && isRestAllowed)
            )
              return true;
            else return false;
          };

          // Crew notes are opt-in per LM type and only apply to pairing LMs. The flag is read from the current
          // definition because the copy embedded on an already-applied LM can predate it. An existing crew note
          // stays editable even if its type has since been opted out, so it can still be changed or cleared.
          const pairingValues = values as IPairingLogicModifier;
          const showCrewNote =
            !!eventGroupOptions &&
            (options.find((lm: ILogicModifier) => lm?._id === values?.logicModifier?._id)?.allowCrewNote === true ||
              !!pairingValues?.crewNote?.note);

          return (
            <>
              {contextMenuForm === false ? (
                <>
                  {isPairingEditor && (
                    <Grid item className="event_actions">
                      <Grid container direction="row">
                        <Button
                          className="action_btn"
                          onClick={() => {
                            resetForm(formProps);
                          }}
                        >
                          <AddBox /> Add New
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                  <div style={{ display: "flex", margin: "10px 5px" }}>{formTitle}</div>
                  <Form>
                    <Grid container className="formik_field_container" direction="row" wrap="wrap">
                      <p className="required_fields_label">required fields</p>
                      <Grid container className="required_fields">
                        <Grid item xs={7}>
                          <FormikSelectField
                            label="Logic Modifier"
                            name="logicModifier"
                            options={logicModifierOptions}
                            parse={(opt: any) => {
                              return options?.find((lm: ILogicModifier) => lm?._id === opt?._id) ?? null;
                            }}
                            format={(opt: any) => {
                              return options?.find((lm: ILogicModifier) => lm?._id === opt?._id) ?? null;
                            }}
                            formatOptionLabel={(lm: ILogicModifier) => {
                              return (
                                <>
                                  <Chip
                                    label={lm.title}
                                    size="small"
                                    sx={{
                                      borderRadius: "3px",
                                      fontWeight: 900,
                                      fontSize: "0.8em",
                                      backgroundColor: `#${lm.color}`,
                                      color: invertHexColor(lm.color),
                                      marginRight: "5px",
                                    }}
                                  />
                                  {lm?.description}
                                </>
                              );
                            }}
                            getOptionLabel={(lm: ILogicModifier) => `${lm.title} - ${lm.description}`}
                            onChange={(val: ILogicModifier) => {
                              getSchema(val); // sets schema for new selection (need to ensure any future form validation works)
                              assignLogicModifier(formProps, val); // assigns the logicModifier and injects any default values
                            }}
                            getOptionValue={(blm: ILogicModifier) => blm._id}
                            isSearchable={true}
                            menuPosition={"fixed"}
                            openMenuOnFocus={false}
                            isClearable={false}
                            noOptionsMessage={() => "Search Logic Modifiers..."}
                          />
                        </Grid>
                      </Grid>

                      <Grid item xs={12}>
                        Optional fields
                      </Grid>
                      {eventGroupOptions && (
                        <Grid container>
                          <Grid item xs={7}>
                            <FormikSelectField
                              label="Event Group"
                              name="dutyTime"
                              format={(dutyTime: number) =>
                                returnSelectedDutyGroup(
                                  dutyTime,
                                  fullEventGroupOptions.filter((opt) => !isEventGroupOptionDisabled(opt))
                                )
                              }
                              parse={(option: IEventGroupOptions) => {
                                return option?.dutyTime;
                              }}
                              options={fullEventGroupOptions.filter((opt) => !isEventGroupOptionDisabled(opt))}
                              isSearchable={true}
                              menuPosition={"fixed"}
                              openMenuOnFocus={false}
                              disabled={!formProps?.values?.logicModifier?._id}
                              noOptionsMessage={() => "Search Events..."}
                            />
                          </Grid>
                        </Grid>
                      )}
                      <Grid item xs={7}>
                        <FormikTextField
                          label="Notes"
                          name="note"
                          fullWidth={true}
                          multiline={true}
                          minRows="2"
                          maxRows="5"
                        />
                      </Grid>
                      {showCrewNote && (
                        <Grid item xs={7}>
                          {/* Yellow like the pairing Crew Note so crew-facing text is easy to tell apart from Notes */}
                          <FormikTextField
                            label="Note Visible to Crew"
                            name="crewNote.note"
                            fullWidth={true}
                            multiline={true}
                            minRows="2"
                            maxRows="5"
                            labelSX={{ color: "#ffd351" }}
                            inputProps={{ style: { backgroundColor: "#ffd351", color: "#000", fontWeight: 500 } }}
                          />
                        </Grid>
                      )}
                      <Grid item xs={7}>
                        <CustomFieldsV2
                          schema={customSchema}
                          objectPath="logicModifier.custom"
                          displayHeader={true}
                          mosaicPane={mosaicPane}
                        />
                      </Grid>
                      <Grid item xs={7} sx={{ p: 1 }}>
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          disabled={!formProps.dirty}
                          onClick={(e) => resetForm(formProps)}
                        >
                          Reset
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          size="small"
                          disabled={!isValid || !values.logicModifier || isDutyOnlyAndDefaultEvent()}
                        >
                          {!values?._id ? "Add" : "Modify"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Form>
                </>
              ) : (
                <>
                  <Form>
                    <Grid container spacing={0}>
                      <Grid item xs={12}>
                        <FormikSelectField
                          name="logicModifier"
                          options={logicModifierOptions}
                          parse={(opt: any) => {
                            return options?.find((lm: ILogicModifier) => lm?._id === opt?._id) ?? null;
                          }}
                          format={(opt: any) => {
                            return options?.find((lm: ILogicModifier) => lm?._id === opt?._id) ?? null;
                          }}
                          formatOptionLabel={(lm: ILogicModifier) => {
                            return (
                              <>
                                <Chip
                                  label={lm.title}
                                  size="small"
                                  sx={{
                                    borderRadius: "3px",
                                    fontWeight: 900,
                                    fontSize: "0.8em",
                                    backgroundColor: `#${lm.color}`,
                                    color: invertHexColor(lm.color),
                                    marginRight: "5px",
                                  }}
                                />
                                {lm?.description}
                              </>
                            );
                          }}
                          getOptionLabel={(lm: ILogicModifier) => `${lm.title} - ${lm.description}`}
                          onChange={(val: ILogicModifier) => {
                            getSchema(val); // sets schema for new selection (need to ensure any future form validation works)
                            assignLogicModifier(formProps, val); // assigns the logicModifier and injects any default values
                          }}
                          getOptionValue={(blm: ILogicModifier) => blm._id}
                          isSearchable={true}
                          menuPortalTarget={mosaicPane}
                          menuPosition={"fixed"}
                          openMenuOnFocus={false}
                          isClearable={false}
                          noOptionsMessage={() => "Search Logic Modifiers..."}
                        />
                      </Grid>
                    </Grid>{" "}
                    <Grid item xs={12}>
                      <FormikTextField
                        name="note"
                        fullWidth={true}
                        multiline={true}
                        minRows="2"
                        maxRows="5"
                        placeholder="Optional Notes..."
                      />
                    </Grid>
                    <CustomFieldsV2 schema={customSchema} objectPath="logicModifier.custom" displayHeader={true} />
                    <Grid item xs={12}>
                      <ButtonGroup size="small" variant="contained" fullWidth>
                        <Button
                          color="warning"
                          disabled={!formProps.dirty}
                          onClick={(e) => resetForm(formProps)}
                          sx={{ fontSize: "0.7em" }}
                        >
                          Reset
                        </Button>
                        <Button
                          type="submit"
                          color="success"
                          disabled={!isValid || !values.logicModifier}
                          sx={{ fontSize: "0.7em" }}
                        >
                          {!values?._id ? "Add" : "Modify"}
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  </Form>
                </>
              )}
            </>
          );
        }}
      </Formik>
    </OCLogicModifierForm>
  );
};
